import React from 'react';
import styled from 'styled-components';

import { LinearProgressBar } from '@/ui/ProgressBar';
import { ProgressBarStyled } from '@/ui/ProgressBar/variants/LinearProgressBar/styles';
import { FileUploaderError } from '@/component/FileUpload/types';

const ContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const UploadContainerStyled = styled.div`
  display: flex;
  padding: 32px 32px 16px 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
`;

const LoadingContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
`;

const TextLoaderContainerStyled = styled.div`
  display: flex;
  width: 424px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const UploadProgressWrapperStyled = styled.div`
  display: flex;
  width: 424px;
  padding: 8px 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  box-sizing: border-box;
`;

const ProgressTextStyled = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.typography.title5};
  color: var(--color-mine-shaft);
`;

const WarningStyled = styled.div`
  align-self: stretch;
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

const LinearProgressBarStyled = styled(LinearProgressBar)`
  width: 100%;

  & ${ProgressBarStyled} {
    height: 4px;
    border-radius: 5px;
    background: ${({ theme }) => theme.colors.neutral.container};

    &:after {
      height: 4px;
      border-radius: 5px;
      background: ${({ theme }) => theme.colors.primary.outline};
    }
  }
`;

const ErrorStyled = styled.div`
  ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.colors.error.main};
`;

type FeedbackFileUploadUploadingProps = {
  uploadProgress: number;
  prevUploadProgress?: number;
  uploadError?: FileUploaderError | null;
};

export const FeedbackFileUploadUploading = ({
  uploadProgress,
  prevUploadProgress,
  uploadError,
}: FeedbackFileUploadUploadingProps): JSX.Element => {
  return (
    <ContainerStyled>
      <UploadContainerStyled>
        <LoadingContainerStyled>
          <TextLoaderContainerStyled>
            <UploadProgressWrapperStyled>
              <ProgressTextStyled>Uploading {uploadProgress}%</ProgressTextStyled>
              <LinearProgressBarStyled
                maxValue={100}
                animationStartPercentage={prevUploadProgress}
                value={uploadProgress}
                animated
              />
            </UploadProgressWrapperStyled>
            <WarningStyled>Do not leave this page until the upload is complete, or progress will be lost</WarningStyled>
          </TextLoaderContainerStyled>
        </LoadingContainerStyled>
      </UploadContainerStyled>
      {uploadError && <ErrorStyled>{uploadError.message || uploadError.title}</ErrorStyled>}
    </ContainerStyled>
  );
};
